import { useState } from 'react'
import { toast } from 'sonner'
import { Plus } from 'lucide-react'
import type { AdvertiserBrand } from '@magnetar/admin-api-interfaces'
import { PageHeader } from '@/components/PageHeader'
import { DeleteAlert } from '@/components/DeleteAlert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/useAuth'
import { useSupplierBrands } from '@/hooks/useSupplierBrands'
import { brandService } from '@/services/brand_service'
import { useViewMode } from '@/stores/viewMode'
import { BrandDialog } from './BrandDialog'
import { SEPARATION_MODE_LABEL } from './separationMode'

/** 供應商的品牌管理表（admin 側）；與 Portal /settings/brands 同一份資料 */
export default function AdSupplierBrandsPage() {
  const { token } = useAuth()
  const { mode, id: viewModeId } = useViewMode()
  const supplierId = mode === 'ad' ? (viewModeId ?? undefined) : undefined
  const { brands, isLoading, isError, mutate } = useSupplierBrands(supplierId)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<AdvertiserBrand | null>(null)
  const [deleting, setDeleting] = useState<AdvertiserBrand | null>(null)

  const usageOf = (b: AdvertiserBrand) => b.usage.advertisements + b.usage.ad_images + b.usage.campaigns

  const handleDelete = async () => {
    if (!token || !supplierId || !deleting) return
    const result = await brandService.remove(supplierId, deleting.brand_id, token)
    if (result.success) {
      toast.success('已刪除品牌')
      mutate()
    } else {
      toast.error(result.error?.message || '刪除失敗')
    }
    setDeleting(null)
  }

  if (!supplierId) {
    return (
      <div className="space-y-6">
        <PageHeader title="品牌管理" description="供應商的品牌與同品牌不相鄰設定" />
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">請先從供應商列表選擇一家供應商。</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="品牌管理"
        description="素材、Banner 圖可以掛品牌；每個品牌可設定同品牌不相鄰播出（梯次內相鄰不播／相鄰梯次不播）。"
        level="section"
      >
        <Button
          onClick={() => {
            setEditing(null)
            setDialogOpen(true)
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          新增品牌
        </Button>
      </PageHeader>

      <Card>
        <CardContent className="p-6">
          {isError ? (
            <p className="py-12 text-center text-destructive">載入品牌失敗，請稍後再試。</p>
          ) : isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : brands.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">還沒有品牌。</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>品牌</TableHead>
                    <TableHead>同品牌不相鄰</TableHead>
                    <TableHead className="text-right">素材</TableHead>
                    <TableHead className="text-right">Banner</TableHead>
                    <TableHead>狀態</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brands.map((b) => (
                    <TableRow key={b.brand_id}>
                      <TableCell className="font-medium">{b.name}</TableCell>
                      <TableCell>
                        <Badge variant={b.separation_mode === 'none' ? 'outline' : 'secondary'}>{SEPARATION_MODE_LABEL[b.separation_mode]}</Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{b.usage.advertisements}</TableCell>
                      <TableCell className="text-right tabular-nums">{b.usage.ad_images}</TableCell>
                      <TableCell>
                        <Badge variant={b.is_active ? 'default' : 'secondary'}>{b.is_active ? '啟用' : '停用'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditing(b)
                              setDialogOpen(true)
                            }}
                          >
                            編輯
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            disabled={usageOf(b) > 0}
                            title={usageOf(b) > 0 ? '還有素材或 Banner 掛著，先改掛或改成停用' : undefined}
                            onClick={() => setDeleting(b)}
                          >
                            刪除
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <BrandDialog open={dialogOpen} onOpenChange={setDialogOpen} supplierId={supplierId} brand={editing} onSaved={() => mutate()} />
      <DeleteAlert
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        onConfirm={handleDelete}
        title={`刪除品牌「${deleting?.name ?? ''}」？`}
        description="沒有素材或 Banner 掛著的品牌才能刪；刪除無法復原。"
      />
    </div>
  )
}
